import React, { useCallback, useEffect, useState } from "react";
import { Formik, Form, FastField } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import CurrencyFieldText from "../../atoms/Inputs/CurrencyField";
import { LGNumber } from "../../atoms/Inputs/LGNumber.tsx";
import { getCatalogue } from "../../api/common/catalogues.ts";
import { getClientPurchaseOrders } from "../../api/clientPurchaseOrders";
import { getFinishedGoodList } from "../../api/finishedGoods";
import {
  createExportItem,
  getExportItemById,
  updateExportItem,
} from "../../api/exports";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { palletDimsFromProps } from "../FinishedGoods/palletProps";

const validationSchema = Yup.object().shape({
  clientPurchaseOrderId: Yup.number().required("Seleccione una orden de compra"),
  finishedGoodId: Yup.number().nullable(),
  itemQuantity: Yup.number().required("Cantidad es obligatoria").positive(),
  palletQuantity: Yup.number()
    .required("Cantidad de pallets es obligatoria")
    .positive(),
  volume: Yup.number().required("Volumen es obligatorio").min(0),
  tariffFractionId: Yup.number().required("Seleccione una fracción arancelaria"),
  unitPrice: Yup.number()
    .required("Precio unitario es obligatorio")
    .min(0),
  netWeight: Yup.number().required("Peso neto es obligatorio").min(0),
  psYear: Yup.number().required("Año PS es obligatorio").min(2000),
  masterNetWeight: Yup.number().min(0),
  masterGrossWeight: Yup.number().min(0),
  piecesPerPallet: Yup.number().min(0),
  mastersPerLayer: Yup.number().min(0),
  piecesPerMaster: Yup.number().min(0),
  palletHeight: Yup.number().min(0),
  palletWidth: Yup.number().min(0),
  palletDepth: Yup.number().min(0),
  palletEmptyWeight: Yup.number().min(0),
  layersPerPallet: Yup.number().min(0),
  piecesPerBed: Yup.number().min(0),
  rfidId: Yup.number().nullable(),
  rfidCost: Yup.number().min(0),
  rfidLaborCost: Yup.number().min(0),
  rfidShipmentCost: Yup.number().min(0),
});

const buildInitialValues = (exportId, companyId) => ({
  companyId,
  exportId,
  clientPurchaseOrderId: null,
  clientPurchaseOrder: null,
  finishedGoodId: null,
  finishedGood: null,
  itemQuantity: 0,
  palletQuantity: 0,
  volume: 0,
  tariffFractionId: "",
  unitPrice: 0,
  netWeight: 0,
  psYear: new Date().getFullYear(),
  masterNetWeight: 0,
  masterGrossWeight: 0,
  piecesPerPallet: 0,
  mastersPerLayer: 0,
  piecesPerMaster: 0,
  palletHeight: 0,
  palletWidth: 0,
  palletDepth: 0,
  palletEmptyWeight: 0,
  layersPerPallet: 0,
  piecesPerBed: 0,
  rfidId: "",
  rfidCost: 0,
  rfidLaborCost: 0,
  rfidShipmentCost: 0,
  agsExportItem: {
    markdownPercentage: "",
    unitCostOverride: "",
  },
});

const ExportItemForm = ({ id, exportId, companyId, onSubmit, onItemsChanged, isAgsExport }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(buildInitialValues(exportId, companyId));
  const { openSnack } = useSnack();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getExportItemById(id);
      const data = res.data;
      setFormData({
        ...data,
        clientPurchaseOrder: data.clientPurchaseOrder || null,
        finishedGood: data.finishedGood || null,
        finishedGoodId: data.finishedGoodId ?? null,
        psYear: data.psYear ?? data.pSYear ?? new Date().getFullYear(),
        // System.Text.Json's camelCase policy only lowercases the leading
        // character, so "RFIDId"/"RFIDCost"/... serialize as "rFIDId"/"rFIDCost"/...
        rfidId: data.rfidId ?? data.rFIDId ?? "",
        rfidCost: data.rfidCost ?? data.rFIDCost ?? 0,
        rfidLaborCost: data.rfidLaborCost ?? data.rFIDLaborCost ?? 0,
        rfidShipmentCost: data.rfidShipmentCost ?? data.rFIDShipmentCost ?? 0,
        agsExportItem: {
          markdownPercentage: data.agsExportItem?.markdownPercentage ?? "",
          unitCostOverride: data.agsExportItem?.unitCostOverride ?? "",
        },
      });
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setLoading(false);
    }
  }, [id, openSnack]);

  useEffect(() => {
    if (formMode === "edit") {
      fetchData();
    } else {
      setFormData(buildInitialValues(exportId, companyId));
    }
  }, [id, exportId, companyId, formMode, fetchData]);

  const handleSubmit = async (values) => {
    const payload = {
      companyId,
      exportId,
      clientPurchaseOrderId: Number(values.clientPurchaseOrderId),
      finishedGoodId:
        values.finishedGoodId === "" || values.finishedGoodId == null
          ? null
          : Number(values.finishedGoodId),
      itemQuantity: Number(values.itemQuantity),
      palletQuantity: Number(values.palletQuantity),
      volume: Number(values.volume),
      tariffFractionId: Number(values.tariffFractionId),
      unitPrice: Number(values.unitPrice),
      netWeight: Number(values.netWeight),
      psYear: Number(values.psYear),
      masterNetWeight: Number(values.masterNetWeight),
      masterGrossWeight: Number(values.masterGrossWeight),
      piecesPerPallet: Number(values.piecesPerPallet),
      mastersPerLayer: Number(values.mastersPerLayer),
      piecesPerMaster: Number(values.piecesPerMaster),
      palletHeight: Number(values.palletHeight),
      palletWidth: Number(values.palletWidth),
      palletDepth: Number(values.palletDepth),
      palletEmptyWeight: Number(values.palletEmptyWeight),
      layersPerPallet: Number(values.layersPerPallet),
      piecesPerBed: Number(values.piecesPerBed),
      rfidId: values.rfidId === "" ? null : Number(values.rfidId),
      rfidCost: Number(values.rfidCost),
      rfidLaborCost: Number(values.rfidLaborCost),
      rfidShipmentCost: Number(values.rfidShipmentCost),
      agsExportItem: isAgsExport
        ? {
            markdownPercentage:
              values.agsExportItem.markdownPercentage === ""
                ? null
                : Number(values.agsExportItem.markdownPercentage),
            unitCostOverride:
              values.agsExportItem.unitCostOverride === ""
                ? null
                : Number(values.agsExportItem.unitCostOverride),
          }
        : null,
    };

    try {
      if (formMode === "insert") {
        await createExportItem(payload);
        openSnack("Item de exportación creado exitosamente", "success");
      } else {
        const { companyId: _c, exportId: _e, ...updatePayload } = payload;
        await updateExportItem(id, {
          id: Number(id),
          clientPurchaseOrderId: updatePayload.clientPurchaseOrderId,
          ...updatePayload,
        });
        openSnack("Item de exportación actualizado exitosamente", "success");
      }
      if (onSubmit) onSubmit();
      if (onItemsChanged) onItemsChanged();
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <Grid container spacing={2} sx={{ minWidth: 720, p: 1 }}>
            <Grid item xs={12}>
              <Typography variant="h6">Facturación y orden</Typography>
            </Grid>
            <Grid item xs={6}>
              <LGAutocomplete
                label="Orden de Compra Cliente"
                fetchFunction={getClientPurchaseOrders}
                fetchBy="search"
                optionLabel={["code"]}
                value={values.clientPurchaseOrder || null}
                onChange={(event, value) => {
                  setFieldValue("clientPurchaseOrderId", value?.id || "");
                  setFieldValue("clientPurchaseOrder", value || null);
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <LGAutocomplete
                label="Producto Terminado"
                fetchFunction={getFinishedGoodList}
                fetchBy="search"
                queryParams={{ companyId, pageSize: 1000 }}
                optionLabel={["code", "name"]}
                optionSeparator=" | "
                value={values.finishedGood || null}
                onChange={(event, value) => {
                  setFieldValue("finishedGoodId", value?.id || null);
                  setFieldValue("finishedGood", value || null);
                  if (value) {
                    setFieldValue("masterNetWeight", value.masterNetWeight ?? 0);
                    setFieldValue("masterGrossWeight", value.masterGrossWeight ?? 0);
                    setFieldValue("piecesPerMaster", value.piecesPerMaster ?? 0);
                    setFieldValue("piecesPerBed", value.piecesPerBed ?? 0);
                    setFieldValue("piecesPerPallet", value.piecesPerPallet ?? 0);
                    setFieldValue("mastersPerLayer", value.mastersPerLayer ?? 0);
                    setFieldValue("layersPerPallet", value.layersPerPallet ?? 0);
                    setFieldValue("palletHeight", value.palletHeight ?? 0);
                    {
                      const fromPallet = palletDimsFromProps(
                        value.pallet?.props ?? value.pallet?.Props
                      );
                      setFieldValue(
                        "palletWidth",
                        fromPallet?.palletWidth ?? value.palletWidth ?? 0
                      );
                      setFieldValue(
                        "palletDepth",
                        fromPallet?.palletDepth ?? value.palletDepth ?? 0
                      );
                      setFieldValue(
                        "palletEmptyWeight",
                        fromPallet?.palletEmptyWeight ??
                          value.palletEmptyWeight ??
                          0
                      );
                    }
                    if (value.unitPrice != null) {
                      setFieldValue("unitPrice", value.unitPrice);
                    }
                    if (value.tariffFractionId) {
                      setFieldValue("tariffFractionId", value.tariffFractionId);
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FastField
                name="tariffFractionId"
                label="Fracción Arancelaria"
                as={DropdownSelector}
                fetchFunction={getCatalogue}
                fetchParams={{ catalogueName: "TARIFF_FRACTION", pageSize: 1000, orderBy: "code" }}
                optionLabel={["code", "name"]}
                optionSeparator=" | "
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Cantidades</Typography>
            </Grid>
            <Grid item xs={4}>
              <FastField
                fullWidth
                name="unitPrice"
                label="Precio unitario"
                component={CurrencyFieldText}
              />
            </Grid>
            <Grid item xs={3}>
              <FastField
                name="itemQuantity"
                label="Cantidad"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <FastField
                name="palletQuantity"
                label="Cantidad de Pallets"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <FastField name="volume" label="Volumen (m³)" as={LGNumber} fullWidth />
            </Grid>
            <Grid item xs={3}>
              <FastField name="netWeight" label="Peso Neto" as={LGNumber} fullWidth />
            </Grid>
            <Grid item xs={3}>
              <FastField name="psYear" label="Año PS" as={LGNumber} fullWidth />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Empaque por caja / tarima</Typography>
              <Typography variant="body2" color="text.secondary">
                Usado por los generadores de Factura Comercial y Packing List de
                exportación (división de tarimas, pesos y dimensiones).
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="masterNetWeight"
                label="Peso neto caja maestra"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="masterGrossWeight"
                label="Peso bruto caja maestra"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="palletEmptyWeight"
                label="Peso de tarima vacía"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="palletHeight"
                label="Alto de tarima"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="palletWidth"
                label="Ancho de tarima"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="palletDepth"
                label="Profundidad de tarima"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="piecesPerMaster"
                label="Piezas por caja maestra"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="mastersPerLayer"
                label="Cajas maestras por cama"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="layersPerPallet"
                label="Camas por tarima"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="piecesPerBed"
                label="Piezas por cama"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="piecesPerPallet"
                label="Piezas por tarima"
                as={LGNumber}
                fullWidth
              />
            </Grid>

            {(values.piecesPerMaster > 0 || values.mastersPerLayer > 0) && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Peso por pieza:{" "}
                  {values.piecesPerMaster > 0
                    ? (
                        Number(values.masterNetWeight) /
                        Number(values.piecesPerMaster)
                      ).toFixed(4)
                    : "—"}{" "}
                  | Cajas por tarima:{" "}
                  {Number(values.mastersPerLayer) *
                    Number(values.layersPerPallet) || "—"}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="h6">RFID</Typography>
              <Typography variant="body2" color="text.secondary">
                Un RFID se factura pero no se surte ni se paletiza — alimenta el
                desglose de la factura comercial, nunca el packing list.
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="rfidId"
                label="RFID"
                as={DropdownSelector}
                fetchFunction={getCatalogue}
                fetchParams={{ catalogueName: "RFID", pageSize: 1000 }}
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="rfidCost"
                label="Costo del RFID (por pieza)"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="rfidLaborCost"
                label="Costo de mano de obra (por pieza)"
                as={LGNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="rfidShipmentCost"
                label="Costo de envío del RFID (por pieza)"
                as={LGNumber}
                fullWidth
              />
            </Grid>

            {isAgsExport && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6">AGS</Typography>
                </Grid>
                <Grid item xs={6}>
                  <FastField
                    name="agsExportItem.markdownPercentage"
                    label="% de Descuento (AGS)"
                    as={LGNumber}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <FastField
                    name="agsExportItem.unitCostOverride"
                    label="Costo Unitario Override (AGS)"
                    as={LGNumber}
                    fullWidth
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Button type="submit" variant="contained" fullWidth>
                {formMode === "edit" ? "Actualizar" : "Crear"} Elemento
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ExportItemForm;
